import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Plus, MapPin, Pencil, Package, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";

const empty = { container: "", shelf: "", rack: "", row: "", bin: "", cubby: "", notes: "" };
const FIELDS = [["container", "Container / Stockroom"], ["shelf", "Shelf"], ["rack", "Rack"], ["row", "Row"], ["bin", "Bin"], ["cubby", "Cubby"]];

export const LocationsSection = ({ currentUser, canManage }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try { setLocations(await inventoryApi.getLocations()); } catch { toast.error("Failed to load locations"); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const openAdd = () => { setForm(empty); setEditId(null); setOpen(true); };
  const openEdit = (l) => { setForm({ ...empty, ...l }); setEditId(l.id); setOpen(true); };

  const save = async () => {
    if (!form.container?.trim()) { toast.error("Container / Stockroom is required"); return; }
    try {
      if (editId) { await inventoryApi.updateLocation(editId, { ...form, updated_by: userName }); toast.success("Location updated"); }
      else { await inventoryApi.createLocation({ ...form, created_by: userName }); toast.success("Location added"); }
      setOpen(false); fetchLocations();
    } catch { toast.error("Failed to save location"); }
  };

  return (
    <div className="space-y-4" data-testid="locations-section">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{locations.length} locations</span>
        {canManage && <Button onClick={openAdd} data-testid="add-location-btn"><Plus className="w-4 h-4 mr-1" /> Add Location</Button>}
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div> : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {locations.map((l) => (
            <div key={l.id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between" data-testid={`location-card-${l.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0"><MapPin className="w-5 h-5 text-slate-500" /></div>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 truncate">{l.label || l.container}</div>
                  <div className="text-xs text-slate-500 inline-flex items-center gap-1"><Package className="w-3 h-3" />{l.part_count} parts</div>
                </div>
              </div>
              {canManage && <button onClick={() => openEdit(l)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg flex-shrink-0"><Pencil className="w-4 h-4" /></button>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="location-dialog">
          <DialogTitle className="sr-only">{editId ? "Edit Location" : "Add Location"}</DialogTitle>
          <DialogDescription className="sr-only">Inventory location form</DialogDescription>
          <h3 className="text-lg font-semibold mb-3">{editId ? "Edit Location" : "Add Location"}</h3>
          <p className="text-xs text-slate-500 mb-3">Build the hierarchy. Only Container / Stockroom is required.</p>
          <div className="space-y-3">
            {FIELDS.map(([k, lbl]) => (
              <div key={k}><Label className="text-xs text-slate-500">{lbl}{k === "container" ? " *" : ""}</Label><Input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`location-${k}-input`} /></div>
            ))}
            <div className="flex gap-2"><Button variant="outline" className="flex-1" onClick={() => setOpen(false)}>Cancel</Button><Button className="flex-1" onClick={save} data-testid="location-save">Save</Button></div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
